// One search across Hive for the app and the MCP: keyword search (search_items)
// and search by meaning (search_meaning, with the query embedded by the `embed`
// Edge Function), blended so exact matches still come first.
import type { HiveClient } from './client.js';
import type { Database } from './database.types.js';

export type SearchResultRow = Database['public']['CompositeTypes']['search_result'];

/** 'both' blends keyword and meaning; 'keyword' is exact words only; 'meaning' ignores the words. */
export type SearchMode = 'both' | 'keyword' | 'meaning';
export const SEARCH_MODES: readonly SearchMode[] = ['both', 'keyword', 'meaning'];

/** Why an item matched: its words, its meaning, or both. */
export type MatchedBy = 'words' | 'meaning' | 'both';

export type SearchHitRow = SearchResultRow & { matched: MatchedBy };

interface Keyed {
  item_type: string | null;
  item_id: string | null;
}

const key = (h: Keyed) => `${h.item_type}:${h.item_id}`;

/** How much a keyword rank counts against a meaning rank. Exact words win ties. */
const WEIGHTS = { words: 1, meaning: 0.85 };
/** The usual reciprocal-rank-fusion constant: damps the gap between first and tenth place. */
const RRF_K = 60;

/**
 * Blends two ranked lists with reciprocal rank fusion. An item in both lists
 * rises above either alone; a keyword hit keeps its highlighted snippet.
 */
export function fuseHits<T extends Keyed>(
  words: readonly T[],
  meaning: readonly T[],
  limit: number,
): (T & { matched: MatchedBy })[] {
  const scored = new Map<string, { hit: T; score: number; matched: MatchedBy }>();
  words.forEach((hit, i) => {
    scored.set(key(hit), { hit, score: WEIGHTS.words / (RRF_K + i + 1), matched: 'words' });
  });
  meaning.forEach((hit, i) => {
    const score = WEIGHTS.meaning / (RRF_K + i + 1);
    const seen = scored.get(key(hit));
    if (seen) {
      seen.score += score;
      seen.matched = 'both';
    } else {
      scored.set(key(hit), { hit, score, matched: 'meaning' });
    }
  });
  return [...scored.values()]
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => ({ ...s.hit, matched: s.matched }));
}

/** A search's own vector, from the `embed` Edge Function (as a member). */
export async function embedQuery(client: HiveClient, text: string): Promise<number[]> {
  const { data, error } = await client.functions.invoke<{ embedding?: number[]; error?: string }>('embed', {
    body: { action: 'query', text },
  });
  if (error) throw new Error(`Search by meaning is unavailable: ${error.message}`);
  if (!data?.embedding?.length) throw new Error(`Search by meaning is unavailable: ${data?.error ?? 'no vector'}`);
  return data.embedding;
}

export interface SearchOptions {
  query: string;
  mode?: SearchMode;
  gameId?: string | null;
  types?: readonly string[] | null;
  limit?: number;
}

export interface SearchOutcome {
  hits: SearchHitRow[];
  /** Set when 'both' fell back to keywords because meaning search failed (say, before it's set up). */
  meaningUnavailable?: string;
}

export async function searchHive(client: HiveClient, options: SearchOptions): Promise<SearchOutcome> {
  const mode = options.mode ?? 'both';
  const limit = Math.min(Math.max(options.limit ?? 20, 1), 100);
  const filters = {
    max_results: limit,
    ...(options.gameId ? { game_id: options.gameId } : {}),
    ...(options.types?.length ? { item_types: [...options.types] } : {}),
  };

  const words = async () => {
    if (mode === 'meaning') return [];
    const { data, error } = await client.rpc('search_items', { query: options.query, ...filters });
    if (error) throw new Error(`Searching: ${error.message}`);
    return data;
  };
  const meaning = async () => {
    if (mode === 'keyword') return [];
    const vector = await embedQuery(client, options.query);
    const { data, error } = await client.rpc('search_meaning', {
      query_embedding: JSON.stringify(vector),
      ...filters,
    });
    if (error) throw new Error(`Searching by meaning: ${error.message}`);
    return data;
  };

  const [w, m] = await Promise.allSettled([words(), meaning()]);
  if (w.status === 'rejected') throw w.reason;
  if (m.status === 'rejected') {
    if (mode === 'meaning') throw m.reason;
    const reason = m.reason instanceof Error ? m.reason.message : String(m.reason);
    return { hits: w.value.map((h) => ({ ...h, matched: 'words' as const })), meaningUnavailable: reason };
  }
  return { hits: fuseHits(w.value, m.value, limit) };
}
